import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable, Subscription, timer } from 'rxjs';
import { LotWatchRequestInspectionData } from 'src/app/services/auction-video-watch/types/lot-watch-request-inspection-data.type';

import { Lot, LotSource, LotTypeEnum } from '../../graphql/service/graphql-auto-main-service';
import { LotWatchRequestFormModalComponent } from './components/lot-watch-request-form-modal/lot-watch-request-form-modal.component';
import { AuctionService } from '../../../services/auction.service';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { AuctionData } from '../../graphql/service/graphql-directory-service';
import { LotWatchRequestTimeLimitService } from '../../../services/lot-watch-request-time-limit.service';

@Component({
  selector: 'app-lot-watch-request-button',
  templateUrl: './lot-watch-request-button.component.html',
  styleUrls: ['./lot-watch-request-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotWatchRequestButtonComponent implements OnInit, OnDestroy {
  @Input() savedLot: Lot;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() fullSize = false;
  @Input() inspectionLoading: boolean;
  @Input() inspectionData: LotWatchRequestInspectionData;

  @Output() lotShared = new EventEmitter<void>();

  private lastAuctionData?: AuctionData;

  @HostBinding('style.display')
  private display: 'none' | 'inline-block' = 'none';

  private subscription = Subscription.EMPTY;

  loading$: Observable<boolean>;

  constructor(
    private readonly translate: TranslateService,
    private readonly modalService: NzModalService,
    private readonly auctionService: AuctionService,
    private readonly lotWatchRequestTimeLimitService: LotWatchRequestTimeLimitService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscription = this.auctionService
      .getByName(this.savedLot?.auctionName)
      .pipe(
        tap((auction) => (this.lastAuctionData = auction?.auctionData)),
        switchMap(() =>
          timer(0, 1000).pipe(
            map(() => this.computeCanShow()),
            distinctUntilChanged(),
          ),
        ),
      )
      .subscribe((canShow) => {
        this.display = canShow ? 'inline-block' : 'none';
        this.cdr.markForCheck();
      });
  }

  openWatchRequestModal(): void {
    this.modalService.create({
      nzTitle: this.translate.instant('WATCH_REQUEST.TITLE'),
      nzContent: LotWatchRequestFormModalComponent,
      nzComponentParams: {
        lot: this.savedLot,
        lotType: this.lotType,
        lotSource: this.lotSource,
        lotShared: this.lotShared,
        inspectionData: this.inspectionData,
        auctionData: this.lastAuctionData,
      },
    });
  }

  private computeCanShow(): boolean {
    const inspectionEnabled = this.lastAuctionData?.inspectionEnabled ?? false;

    if (!inspectionEnabled) {
      return false;
    }

    return this.lotWatchRequestTimeLimitService.isInspectionAvailable(
      this.savedLot,
      this.lastAuctionData,
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
